/**
 * IS THERE ANYONE TALKING IN THIS CLIP? Asked of the recording itself, before
 * it is uploaded.
 *
 * Whisper does not fail on silence or on a clip too short to hold a word — it
 * invents filler ("Thanks for watching.", "Thank you.", "you"), and the voice
 * route then runs that as a real turn: the agent answers a question nobody
 * asked, out loud, in front of whoever is watching. The live turn detector
 * already refuses to END a hands-free turn on silence, but three paths never
 * consulted it:
 *
 *   - tap-to-talk, which runs no detector at all (deliberately — see
 *     `ensureAudioContext` in AIAgentScreen),
 *   - a hands-free mic tapped OFF by hand before anything was said,
 *   - a tap-on, tap-off inside half a second.
 *
 * So every clip is judged here, by ONE definition of speech: the decoded
 * samples are fed through the same `TurnDetector` the live mic uses, and the
 * clip is speech only if that detector would have ARMED on it. A second,
 * looser definition living beside the first is how the two drift.
 *
 * ── WHEN THE CLIP CANNOT BE DECODED, IT IS SENT ─────────────────────────────
 * `unknown` keeps today's behaviour rather than discarding. A browser that
 * cannot decode its own recorder's output would otherwise lose every word the
 * user says, silently, which is a far worse failure than the one this guards.
 * The API filters known silence phrases as a second layer.
 */

import { TurnDetector, HANDS_FREE_TURN, type TurnDetectorConfig } from './voice-activity';

/** Under this, a clip is a tap, not a sentence. */
export const MIN_CLIP_MS = 500;

/** How long decoding may take before the clip is sent unjudged. */
export const DECODE_TIMEOUT_MS = 1500;

/** Loudness is read in windows of this size — the same order as the live sampler. */
const FRAME_MS = 20;

/*
 * The live detector, with its two time limits lifted: offline, the whole clip
 * is read and only `armed` matters, so neither ending a turn on silence nor the
 * 30s ceiling may settle the detector before the speech in the clip is reached.
 */
const OFFLINE_TURN: TurnDetectorConfig = {
  ...HANDS_FREE_TURN,
  silenceMs: Number.POSITIVE_INFINITY,
  maxMs: Number.POSITIVE_INFINITY,
};

export type ClipVerdict =
  | { kind: 'speech'; durationMs: number }
  | { kind: 'too_short'; durationMs: number }
  | { kind: 'no_speech'; durationMs: number }
  /** Could not be decoded or judged — send it, as before. */
  | { kind: 'unknown' };

/** Judge raw mono samples. Pure, so every rule is testable with numbers. */
export function judgeSamples(samples: Float32Array, sampleRate: number): ClipVerdict {
  const durationMs = (samples.length / sampleRate) * 1000;
  if (durationMs < MIN_CLIP_MS) return { kind: 'too_short', durationMs };

  const detector = new TurnDetector(OFFLINE_TURN);
  const frame = Math.max(1, Math.round((sampleRate * FRAME_MS) / 1000));
  for (let start = 0; start < samples.length; start += frame) {
    const end = Math.min(start + frame, samples.length);
    let sum = 0;
    for (let i = start; i < end; i++) sum += samples[i] * samples[i];
    const rms = Math.sqrt(sum / (end - start));
    detector.push(rms, (start / sampleRate) * 1000);
    if (detector.armed) return { kind: 'speech', durationMs };
  }
  return { kind: 'no_speech', durationMs };
}

type Decode = (bytes: ArrayBuffer) => Promise<{ samples: Float32Array; sampleRate: number }>;

/** The browser's decoder. Never needs a user gesture: an offline context does not play. */
const browserDecode: Decode = (bytes) => {
  const Ctor: typeof OfflineAudioContext | undefined =
    (window as any).OfflineAudioContext || (window as any).webkitOfflineAudioContext;
  if (!Ctor) return Promise.reject(new Error('no OfflineAudioContext'));
  const ctx = new Ctor(1, 1, 16000);
  return new Promise((resolve, reject) => {
    // Both forms: older Safari only reports through the callbacks.
    const done = (buf: AudioBuffer) => resolve({ samples: buf.getChannelData(0), sampleRate: buf.sampleRate });
    const p = ctx.decodeAudioData(bytes, done, reject);
    if (p && typeof p.then === 'function') p.then(done, reject);
  });
};

/** `Blob.arrayBuffer` where it exists, a FileReader where it does not (older WebKit). */
function readBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Judge a recorded clip. Never rejects and never hangs: a decoder that fires
 * neither callback (it happens) is raced against a timeout, because a promise
 * that never settles walks straight past a try/catch and the clip would be
 * neither sent nor dropped.
 */
export async function judgeClip(blob: Blob, decode: Decode = browserDecode): Promise<ClipVerdict> {
  // Started before the read, so a read that hangs is covered as well as a decode.
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), DECODE_TIMEOUT_MS));
  try {
    const decoded = await Promise.race([readBytes(blob).then(decode), timeout]);
    if (!decoded) return { kind: 'unknown' };
    return judgeSamples(decoded.samples, decoded.sampleRate);
  } catch {
    return { kind: 'unknown' };
  }
}
